import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Bell, Sparkles, Zap, BookOpen, Award, MessageSquare, FileText, Check, Trash2, Mail, AlertTriangle, ExternalLink } from "lucide-react";
import { MoodSelector } from "@/components/mood/MoodSelector";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { useAdminNotifications, type AdminNotification } from "@/hooks/useAdminNotifications";
import { useUnresolvedErrorCount } from "@/hooks/useUnresolvedErrorCount";
import { prefetchCourseData, prefetchRoute } from "@/hooks/usePrefetchRoute";
import { formatDistanceToNow } from "date-fns";
import type { QuizLearnSetBasic, HomeData } from "@/hooks/useHomeData";

// Target quiz set by title
const STARTER_QUIZ_TITLE = "AI Basics";

interface NotificationItem {
  id: string;
  icon: typeof Sparkles;
  title: string;
  subtitle: string;
  linkTo: string;
  guestOnly?: boolean;
  external?: boolean;
}

const ADMIN_NOTIFICATION_ICONS: Record<AdminNotification["notification_type"], typeof Award> = {
  certificate_issued: Award,
  feedback_received: MessageSquare,
  report_generated: FileText,
};

interface NotificationDropdownProps {
  quizLearnSets?: QuizLearnSetBasic[];
  courses?: HomeData["courses"];
  completedCourseIds?: string[];
}

export function NotificationDropdown({ 
  quizLearnSets, 
  courses,
  completedCourseIds = []
}: NotificationDropdownProps) {
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [showBadge, setShowBadge] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionKey = "notification_interacted";

  // Admin notifications hook
  const {
    notifications: adminNotifications,
    unreadCount: adminUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useAdminNotifications();

  // Error count for admins
  const { count: errorCount } = useUnresolvedErrorCount();

  // Find starter quiz for "New Here" option
  const starterQuiz = quizLearnSets?.find(
    (set) => set.title.toLowerCase().includes(STARTER_QUIZ_TITLE.toLowerCase())
  );
  const starterLink = starterQuiz 
    ? `/quiz-learn?set=${starterQuiz.id}` 
    : "/quiz-learn";

  // Check if user has completed the starter quiz
  const hasCompletedStarter = user && starterQuiz && completedCourseIds.includes(starterQuiz.id);

  // Pick a random beginner-friendly course
  const randomCourse = useMemo(() => {
    if (!courses || courses.length === 0) return null;
    
    const simpleKeywords = ["basics", "introduction", "101", "fundamentals", "getting started", "beginner", "essentials", "overview"];
    
    const simpleCourses = courses.filter(course => {
      if (user && completedCourseIds.includes(course.id)) return false;
      
      const titleLower = course.title.toLowerCase();
      const categoryLower = (course.category || "").toLowerCase();
      
      return simpleKeywords.some(keyword => 
        titleLower.includes(keyword) || categoryLower.includes(keyword)
      );
    });
    
    const pool = simpleCourses.length > 0 
      ? simpleCourses.slice(0, 10) 
      : courses.filter(c => !user || !completedCourseIds.includes(c.id)).slice(0, 10);
    
    if (pool.length === 0) return null;
    
    const randomIndex = Math.floor(Math.random() * pool.length);
    return pool[randomIndex];
  }, [courses, user, completedCourseIds]);

  // Rotation index for cycling suggestion sets
  // Cycle suggestions only after inactivity badge appears
  const [suggestionCycle, setSuggestionCycle] = useState(0);
  useEffect(() => {
    if (!showBadge) return;
    const interval = setInterval(() => setSuggestionCycle(c => c + 1), 12000);
    return () => clearInterval(interval);
  }, [showBadge]);

  // Build suggestion items (for all users) — pool of options, show 3 at a time
  const suggestions = useMemo((): NotificationItem[] => {
    const pool: NotificationItem[] = [];

    // Slot A options — discovery / onboarding
    if (!user || !hasCompletedStarter) {
      pool.push({
        id: "new-here",
        icon: Sparkles,
        title: "New here? Start with the basics",
        subtitle: "Test your AI knowledge",
        linkTo: starterLink,
      });
    }
    pool.push({
      id: "browse-all",
      icon: BookOpen,
      title: "Browse all topics",
      subtitle: "Find something that clicks",
      linkTo: "/browse",
    });
    pool.push({
      id: "learning-paths",
      icon: Award,
      title: "Follow a Learning Path",
      subtitle: "Guided skill-building tracks",
      linkTo: "/browse",
    });

    // Slot B options — quizzes & challenges
    pool.push({
      id: "quiz-learn",
      icon: Zap,
      title: "Test yourself with Quiz & Learn",
      subtitle: "Instant explanations included",
      linkTo: "/quiz-learn",
    });
    pool.push({
      id: "wwyd",
      icon: Zap,
      title: "What Would You Do?",
      subtitle: "Scenarios with personality insights",
      linkTo: "/what-would-you-do",
    });
    pool.push({
      id: "plonbox",
      icon: ExternalLink,
      title: "Plan your goals with plonbox",
      subtitle: "Goal Planner · plonbox.com",
      linkTo: "https://plonbox.com",
      external: true,
    });

    // Slot C options — featured courses
    if (randomCourse) {
      pool.push({
        id: "featured-course",
        icon: BookOpen,
        title: `Try "${randomCourse.title}"`,
        subtitle: "Bite-sized learning cards",
        linkTo: `/learn/${randomCourse.id}`,
      });
    }

    // Pick 3 items by cycling through the pool
    if (pool.length <= 3) return pool;
    const offset = suggestionCycle % Math.max(pool.length - 2, 1);
    const picked = new Set<number>();
    picked.add(0); // always include first discovery item
    // Add offset-based picks
    for (let i = 0; picked.size < 3 && i < pool.length; i++) {
      picked.add((offset + i) % pool.length);
    }
    return Array.from(picked).sort((a, b) => a - b).map(i => pool[i]);
  }, [user, hasCompletedStarter, starterLink, randomCourse, suggestionCycle]);

  // Total badge count: admin unread + errors + suggestion badge if triggered
  const adminTotalCount = adminUnreadCount + (errorCount || 0);
  const totalBadgeCount = isAdmin 
    ? adminTotalCount + (showBadge && suggestions.length > 0 ? suggestions.length : 0)
    : (showBadge ? suggestions.length : 0);
  
  // Determine if we have urgent items (errors)
  const hasUrgentItems = isAdmin && errorCount > 0;

  // Reset inactivity timer on user interaction
  const resetInactivityTimer = useCallback((dismissBadge = false) => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    if (dismissBadge) {
      setShowBadge(false);
    }
    
    if (hasInteracted || sessionStorage.getItem(sessionKey)) return;
    
    // Show badge after 10 seconds of inactivity (silent notification - no toasts)
    inactivityTimerRef.current = setTimeout(() => {
      if (!hasInteracted && !sessionStorage.getItem(sessionKey)) {
        setShowBadge(true);
      }
    }, 10000);
  }, [hasInteracted]);

  // Set up inactivity detection
  useEffect(() => {
    if (sessionStorage.getItem(sessionKey)) {
      setHasInteracted(true);
      return;
    }

    resetInactivityTimer();

    const interactionEvents = ["click", "keydown", "touchstart"];
    const passiveEvents = ["mousemove", "scroll"];
    
    const handleActiveInteraction = () => {
      resetInactivityTimer(true);
    };
    
    const handlePassiveInteraction = () => {
      resetInactivityTimer(false);
    };

    interactionEvents.forEach(event => {
      window.addEventListener(event, handleActiveInteraction, { passive: true });
    });
    passiveEvents.forEach(event => {
      window.addEventListener(event, handlePassiveInteraction, { passive: true });
    });

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      interactionEvents.forEach(event => {
        window.removeEventListener(event, handleActiveInteraction);
      });
      passiveEvents.forEach(event => {
        window.removeEventListener(event, handlePassiveInteraction);
      });
    };
  }, [resetInactivityTimer, showBadge]);

  // Reset badge visibility on route change
  useEffect(() => {
    if (!sessionStorage.getItem(sessionKey)) {
      setShowBadge(false);
      resetInactivityTimer();
    }
  }, [location.pathname, resetInactivityTimer]);

  // Handle dropdown open - mark as interacted
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setShowBadge(false);
      setHasInteracted(true);
      sessionStorage.setItem(sessionKey, "1");
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    }
  };

  // Prefetch on hover
  const handlePrefetch = useCallback((linkTo: string) => {
    if (linkTo.startsWith("/quiz-learn")) {
      prefetchRoute("/quiz-learn");
    } else if (linkTo.startsWith("/learn/")) {
      const courseId = linkTo.replace("/learn/", "");
      prefetchCourseData(queryClient, courseId);
    }
  }, [queryClient]);

  return (
    <div className="flex items-center gap-1.5">
      <MoodSelector />
      <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="secondary" 
            size="sm" 
            className="relative px-2.5 shadow-sm ring-1 ring-border" 
            aria-label="Notifications"
            data-track-cta
          >
            <Bell className="h-5 w-5" />
            {(totalBadgeCount > 0) && (
              <Badge 
                className={`absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 flex items-center justify-center text-[10px] font-bold ${
                  hasUrgentItems ? "bg-destructive text-destructive-foreground" : 
                  adminUnreadCount > 0 ? "bg-destructive text-destructive-foreground" : 
                  "animate-pulse bg-primary text-primary-foreground"
                }`}
              >
                {totalBadgeCount > 9 ? "9+" : totalBadgeCount}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72 bg-popover z-50 p-1.5">
          {/* Suggestions section - for all users */}
          <div className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground px-2 py-1">
            Try these
          </div>
          {suggestions.map((item) => {
            const Icon = item.icon;
            const content = (
              <div className="flex items-center gap-2.5 px-2 py-1.5 rounded-md cursor-pointer w-full">
                <div className="shrink-0 w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center">
                  <Icon className="h-3.5 w-3.5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium leading-snug truncate">
                    {item.title}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {item.subtitle}
                  </p>
                </div>
              </div>
            );
            return (
              <DropdownMenuItem 
                key={item.id} 
                asChild
                onMouseEnter={() => !item.external && handlePrefetch(item.linkTo)}
                onFocus={() => !item.external && handlePrefetch(item.linkTo)}
              >
                {item.external ? (
                  <a href={item.linkTo} target="_blank" rel="noreferrer">
                    {content}
                  </a>
                ) : (
                  <Link to={item.linkTo}>
                    {content}
                  </Link>
                )}
              </DropdownMenuItem>
            );
          })}

          {/* Admin notifications section - only for admins */}
          {isAdmin && (
            <>
              <DropdownMenuSeparator className="my-1" />
              
              {/* Error log link - urgent priority */}
              {errorCount > 0 && (
                <DropdownMenuItem asChild>
                  <Link 
                    to="/admin/error-log" 
                    className="flex items-center gap-2.5 px-2 py-1.5 rounded-md cursor-pointer"
                  >
                    <div className="shrink-0 w-6 h-6 rounded-md bg-destructive/10 flex items-center justify-center">
                      <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium leading-snug text-destructive">
                        {errorCount} Unresolved Error{errorCount !== 1 ? "s" : ""}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        Needs attention
                      </p>
                    </div>
                  </Link>
                </DropdownMenuItem>
              )}
              
              <div className="flex items-center justify-between px-2 py-1">
                <span className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground">
                  Admin Updates
                </span>
                {adminUnreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 text-[10px] px-1.5"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      markAllAsRead();
                    }}
                  >
                    <Check className="h-3 w-3 mr-0.5" />
                    Mark read
                  </Button>
                )}
              </div>

              {adminNotifications.length === 0 && errorCount === 0 ? (
                <div className="py-3 text-center text-xs text-muted-foreground">
                  No admin notifications
                </div>
              ) : adminNotifications.filter(n => !n.is_read).length === 0 ? null : (
                <div className="max-h-[180px] overflow-y-auto space-y-0.5">
                  {adminNotifications.filter(n => !n.is_read).slice(0, 5).map((notification) => {
                    const Icon = ADMIN_NOTIFICATION_ICONS[notification.notification_type];
                    
                    return (
                      <DropdownMenuItem 
                        key={notification.id} 
                        asChild
                        className={`p-0 ${!notification.is_read ? "bg-primary/5" : ""}`}
                      >
                        <div className="flex items-start gap-1.5 p-1.5 rounded-md cursor-pointer w-full">
                          <Link 
                            to="/admin/mailbox"
                            className="flex items-start gap-1.5 flex-1 min-w-0"
                            onClick={() => {
                              if (!notification.is_read) {
                                markAsRead(notification.id);
                              }
                            }}
                          >
                            <div className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center ${
                              notification.notification_type === "certificate_issued" 
                                ? "bg-yellow-500/10 text-yellow-600" 
                                : notification.notification_type === "feedback_received"
                                ? "bg-blue-500/10 text-blue-600"
                                : "bg-green-500/10 text-green-600"
                            }`}>
                              <Icon className="h-3.5 w-3.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs leading-snug truncate ${!notification.is_read ? "font-medium" : ""}`}>
                                {notification.title}
                              </p>
                              <p className="text-[10px] text-muted-foreground">
                                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                              </p>
                            </div>
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 shrink-0 opacity-50 hover:opacity-100"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </DropdownMenuItem>
                    );
                  })}
                </div>
              )}

              <DropdownMenuSeparator className="my-1" />
              <DropdownMenuItem asChild>
                <Link 
                  to="/admin/mailbox" 
                  className="flex items-center justify-center gap-1.5 text-xs text-primary"
                >
                  <Mail className="h-3.5 w-3.5" />
                  Open Mailbox
                </Link>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
